import { Component, createRef } from 'react';
import { RejectedFile, SuggestedPage } from '../../../services/file-validation.service';
import { MetricsService } from '../../../services/metrics.service';
import { MetricEventName } from '../../../types/metric-events';
import { container } from '../../../context/module';
import styles from './validation-banner.module.scss';
import { transferTray } from '../../../services/transfer-tray.service';

/** Three is what the design already shows in the wild; eight in a list is a menu, not a suggestion. */
const VISIBLE_DESTINATIONS = 3;

interface ValidationBannerProps {
    rejectedFiles: RejectedFile[];
    totalFiles: number;
    toolName: string;
    /** Slug of the page the user is leaving, for hand-off telemetry. */
    fromSlug?: string;
    acceptedExtensions: string[];
    /**
     * Called when the last pending row is removed. Dismissing the banner does NOT call this:
     * dismissing hides the explanation, it does not throw the user's files away.
     */
    onDismiss: () => void;
}

interface ValidationBannerState {
    /** false = S4: the explanation is gone but the pending rows stay. */
    bannerVisible: boolean;
    /** Pointer to the stashed files, appended to every suggestion link once ready. */
    xferId: string | null;
    /** Rows the user explicitly removed. Keyed by the same key the rows render under. */
    removed: Set<string>;
    /** Groups whose destination was opened in a new tab — MSG-12, the row goes quiet. */
    handedOff: Set<string>;
    /** Groups whose "More options" disclosure is open. */
    expanded: Set<string>;
}

/** A set of misrouted files that share an extension and therefore share a destination list. */
interface PendingGroup {
    key: string;
    /** Extensions merged into this group — more than one when they resolve to the same tool. */
    extensions: string[];
    files: RejectedFile[];
    suggestions: SuggestedPage[];
    status: 'loading' | 'loaded' | 'failed';
}

/**
 * The misrouted half of a mixed batch.
 *
 * The rule that shapes everything here: a file the user selected is work they have already done,
 * and this component is the only thing standing between that work and the bin. So a row survives
 * until the user removes it — including across a banner dismissal (S3 → S4 in
 * docs/proposed/UX_SPEC_UPLOAD_AND_PREVIEW.md §1.1), because dismissing an explanation is not
 * the same act as discarding files.
 */
export class ValidationBanner extends Component<ValidationBannerProps, ValidationBannerState> {
    state: ValidationBannerState = {
        bannerVisible: true,
        xferId: null,
        removed: new Set(),
        handedOff: new Set(),
        expanded: new Set(),
    };

    /** Row refs, so removing one can move focus to its neighbour rather than dropping it to <body>. */
    private rowRefs = new Map<string, HTMLLIElement | null>();
    private regionRef = createRef<HTMLDivElement>();

    componentDidMount() {
        // Stash the rejected files up front rather than on click, so the href already
        // carries the pointer. That keeps the suggestions real anchors — middle-click and
        // Cmd-click open a new tab and still bring the file with them.
        const files = this.props.rejectedFiles.map(rf => rf.file).filter(Boolean);
        transferTray
            .put(files, this.props.fromSlug || '', this.props.toolName || '')
            .then(xferId => {
                if (!xferId) return;
                this.setState({ xferId });
                try {
                    container.get(MetricsService).emit(MetricEventName.FILE_HANDOFF_STARTED, {
                        xferId, fromPage: this.props.fromSlug || '', fileCount: files.length,
                        bytes: files.reduce((n, f) => n + (f?.size || 0), 0),
                    });
                } catch { /* telemetry is never load-bearing */ }
            })
            .catch(() => { /* hand-off is best-effort; navigation must never block on it */ });
    }

    /** Destination URL with the hand-off pointer when we have one. */
    private hrefFor(url: string): string {
        if (!this.state.xferId) return url;
        return `${url}${url.includes('?') ? '&' : '?'}xfer=${encodeURIComponent(this.state.xferId)}`;
    }

    /**
     * Misrouted files grouped by the destination list they share.
     *
     * Grouped by extension first, because that is how suggestions are fetched — then groups
     * whose top destination is identical are merged, so two extensions that both belong on
     * the same tool produce one row and one action rather than two of each.
     */
    private groups(): PendingGroup[] {
        const byExtension = new Map<string, RejectedFile[]>();
        for (const rf of this.props.rejectedFiles) {
            if (this.state.removed.has(this.keyForFile(rf))) continue;
            const list = byExtension.get(rf.extension) || [];
            list.push(rf);
            byExtension.set(rf.extension, list);
        }

        const merged = new Map<string, PendingGroup>();
        for (const [ext, files] of byExtension) {
            const suggestions = files[0].suggestedPages || [];
            const status = files[0].suggestionStatus
                || (suggestions.length > 0 ? 'loaded' : 'loading');
            // Merge on the whole visible destination list, not just the top one: two extensions
            // offering the same three tools are the same choice presented twice.
            const mergeKey = suggestions.slice(0, VISIBLE_DESTINATIONS).map(s => s.url).join('|') || `ext:${ext}`;
            const existing = merged.get(mergeKey);
            if (existing) {
                existing.extensions.push(ext);
                existing.files.push(...files);
            } else {
                merged.set(mergeKey, { key: mergeKey, extensions: [ext], files, suggestions, status });
            }
        }
        return [...merged.values()];
    }

    /**
     * The one destination that works for the whole batch.
     *
     * A person who drops a PNG and a JPG on the GIF compressor is not asking two questions --
     * they want their images compressed. Answering with two boxes and six links makes them pick
     * a tool per file and run the job twice. When every group shares a destination (the generic
     * "Compress Image" now that ranking favours the action over this page's output format), it
     * is offered once, at the top, and takes all of them.
     *
     * Null when the groups genuinely disagree; then the per-format lists are the honest answer.
     */
    private batchDestination(groups: PendingGroup[]): SuggestedPage | null {
        if (groups.length < 2) return null;
        const [first, ...rest] = groups;
        for (const candidate of first.suggestions) {
            const inAll = rest.every(g => g.suggestions.some(s => s.url === candidate.url));
            if (inAll) return candidate;
        }
        return null;
    }

    private keyForFile(rf: RejectedFile): string {
        return `${rf.file?.name || ''}:${rf.file?.size ?? ''}:${rf.extension}`;
    }

    private remainingCount(): number {
        return this.props.rejectedFiles.filter(rf => !this.state.removed.has(this.keyForFile(rf))).length;
    }

    /** Files that made it through — the batch minus what was rejected. */
    private acceptedCount(): number {
        return this.props.totalFiles - this.props.rejectedFiles.length;
    }

    dismiss = () => {
        try {
            container.get(MetricsService).emit(MetricEventName.VALIDATION_BANNER_DISMISSED, {
                pageSlug: this.props.fromSlug || '',
                rejectedCount: this.remainingCount(),
            });
        } catch { /* telemetry is never load-bearing */ }
        // S3 → S4: the explanation goes, the rows stay. Calling onDismiss here would clear
        // them from the parent, which is the file-discarding bug this component exists to fix.
        this.setState({ bannerVisible: false });
    };

    private removeGroup = (group: PendingGroup) => {
        const keys = group.files.map(f => this.keyForFile(f));
        const ordered = this.groups();
        const idx = ordered.findIndex(g => g.key === group.key);
        const neighbour = ordered[idx + 1] || ordered[idx - 1];

        const removed = new Set(this.state.removed);
        keys.forEach(k => removed.add(k));
        this.setState({ removed }, () => {
            if (this.remainingCount() === 0) {
                // Nothing left to keep — hand control back so the drop zone can take over.
                this.props.onDismiss();
                return;
            }
            // Focus must land somewhere deliberate; otherwise removing a row drops the
            // keyboard user to the top of the document.
            const next = neighbour && this.rowRefs.get(neighbour.key);
            (next || this.regionRef.current)?.focus?.();
        });
    };

    private onDestinationClick(group: PendingGroup, page: SuggestedPage, rank: number, newTab: boolean) {
        try {
            container.get(MetricsService).emit(MetricEventName.SUGGESTION_LINK_CLICKED, {
                fromPage: this.props.toolName, toPage: page.url,
                fileExtension: group.extensions.join(','), suggestionRank: rank,
                carriedFile: !!this.state.xferId,
            });
        } catch { /* telemetry is never load-bearing */ }
        if (newTab) {
            // MSG-12. The row does not vanish: the user needs to see where the file went,
            // especially when the destination opened behind the current tab.
            const handedOff = new Set(this.state.handedOff);
            handedOff.add(group.key);
            this.setState({ handedOff });
        }
    }

    private toggleExpanded(key: string) {
        const expanded = new Set(this.state.expanded);
        if (expanded.has(key)) expanded.delete(key); else expanded.add(key);
        this.setState({ expanded });
    }

    private renderDestinations(group: PendingGroup, newTab: boolean, batch?: SuggestedPage | null) {
        if (group.status === 'failed') {
            // MSG-11b. Previously this state was indistinguishable from "still loading", so a
            // failed lookup left "Loading suggestions..." on screen for the rest of the session.
            return (
                <div className={styles.suggestionLoading}>
                    We couldn&apos;t look up a better tool right now.{' '}
                    <a href="/all-tools">Search all tools</a>.
                </div>
            );
        }
        if (group.suggestions.length === 0) {
            return <div className={styles.suggestionLoading}>Finding the right tool…</div>;
        }

        // Already offered once above for the whole batch; repeating it per file is noise.
        const suggestions = batch ? group.suggestions.filter(sg => sg.url !== batch.url) : group.suggestions;
        if (suggestions.length === 0) return null;
        const isExpanded = this.state.expanded.has(group.key);
        const shown = isExpanded ? suggestions : suggestions.slice(0, VISIBLE_DESTINATIONS);
        const hidden = suggestions.length - VISIBLE_DESTINATIONS;

        return (
            <>
                {shown.map((page, i) => (
                    <a key={page.url} href={this.hrefFor(page.url)} className={styles.suggestionItem}
                        /* If valid files are already staged here, the user has work in
                           progress (possibly an upload still running). Sending them away
                           is the one thing we cannot undo, so open a new tab instead. */
                        {...(newTab ? { target: '_blank', rel: 'noopener' } : {})}
                        onClick={() => this.onDestinationClick(group, page, i, newTab)}>
                        <span className={styles.suggestionTitle}>{page.title}</span>
                        <span className={styles.suggestionCta}>Go →</span>
                    </a>
                ))}
                {hidden > 0 && (
                    <button type="button" className={styles.moreOptions}
                        aria-expanded={isExpanded}
                        onClick={() => this.toggleExpanded(group.key)}>
                        {isExpanded ? 'Fewer options' : `More options (${hidden})`}
                    </button>
                )}
            </>
        );
    }

    private renderRow(group: PendingGroup, showGroupLabel: boolean, newTab: boolean, batch?: SuggestedPage | null) {
        const handedOff = this.state.handedOff.has(group.key);
        const names = group.files.map(f => f.file?.name).filter(Boolean);
        return (
            <li key={group.key} className={`${styles.pendingRow} ${handedOff ? styles.pendingRowQuiet : ''}`}
                tabIndex={-1} ref={el => { this.rowRefs.set(group.key, el); }}>
                <div className={styles.pendingRowHead}>
                    <span className={styles.pendingRowNames}>{names.join(', ')}</span>
                    <button type="button" className={styles.removeRow}
                        aria-label={`Remove ${names.join(', ')}`}
                        onClick={() => this.removeGroup(group)}>
                        Remove
                    </button>
                </div>
                {handedOff ? (
                    <div className={styles.handedOff}>✓ Opened in a new tab</div>
                ) : (
                    <>
                        {showGroupLabel && (
                            <div className={styles.suggestionGroupLabel}>
                                For your {group.extensions.map(e => `.${e}`).join(' and ')}{' '}
                                {group.files.length === 1 ? 'file' : 'files'}:
                            </div>
                        )}
                        {this.renderDestinations(group, newTab, batch)}
                    </>
                )}
            </li>
        );
    }

    render() {
        if (this.props.rejectedFiles.length === 0) return null;
        const groups = this.groups();
        if (groups.length === 0) return null;

        const remaining = this.remainingCount();
        const batch = this.batchDestination(groups);
        const acceptedCount = this.acceptedCount();
        // Sending the user away is only unsafe when they have work staged here.
        const newTab = acceptedCount > 0;
        // "file" is an internal wildcard extension (drives the generic convert-file-to-*
        // routes) — not a real format, so it's never surfaced in this list.
        const displayExts = this.props.acceptedExtensions.filter(e => e.toLowerCase() !== 'file');
        const acceptedExtsDisplay = displayExts.length <= 6
            ? displayExts.map(e => e.toUpperCase()).join(', ')
            : `${displayExts.slice(0, 5).map(e => e.toUpperCase()).join(', ')} and ${displayExts.length - 5} more`;

        return (
            <div className={`${styles.banner} ${this.state.bannerVisible ? styles.enter : styles.quiet}`}
                ref={this.regionRef} tabIndex={-1}
                role="group" aria-labelledby="validation-banner-heading">
                {this.state.bannerVisible ? (
                    <>
                        <div className={styles.header}>
                            <span id="validation-banner-heading">
                                ⚠️ {remaining === 1 && this.props.totalFiles === 1
                                    ? '1 file was not uploaded'
                                    /* Always show the batch context, including for a single rejected
                                       file out of several — "1 of 5" is the fact that tells the user
                                       their other four are fine. */
                                    : `${remaining} of ${this.props.totalFiles} files were not uploaded`}
                            </span>
                            <button className={styles.dismiss} onClick={this.dismiss} aria-label="Dismiss">✕</button>
                        </div>
                        <div className={styles.body}>
                            <p>
                                {remaining === 1
                                    ? `This file can't be used with ${this.props.toolName}.`
                                    : `These files can't be used with ${this.props.toolName}:`}
                                {acceptedExtsDisplay && ` It accepts ${acceptedExtsDisplay} files.`}
                            </p>
                            {acceptedCount > 0 && (
                                <p>{acceptedCount} supported file{acceptedCount > 1 ? 's were' : ' was'} uploaded successfully.</p>
                            )}
                            <p>{batch ? 'Here\'s where they belong:' : 'Try one of these instead:'}</p>
                        </div>
                    </>
                ) : (
                    // MSG-14. The rows are still here; only the explanation was dismissed.
                    <div id="validation-banner-heading" className={styles.quietLine}>
                        {`${remaining} ${remaining === 1 ? 'file' : 'files'} below can't be used on this page.`}
                    </div>
                )}

                {batch && (
                    <div className={styles.batchRow}>
                        <a href={this.hrefFor(batch.url)}
                            className={styles.batchLink}
                            {...(newTab ? { target: '_blank', rel: 'noopener' } : {})}
                            onClick={() => this.onDestinationClick(groups[0], batch, 0, newTab)}>
                            <span className={styles.suggestionTitle}>{batch.title}</span>
                            <span className={styles.suggestionCta}>Go →</span>
                        </a>
                        <span className={styles.batchHint}>
                            Takes {remaining === 1 ? 'your file' : `all ${remaining} files`} at once.
                        </span>
                    </div>
                )}
                <ul className={styles.pendingRows}>
                    {groups.map(g => this.renderRow(g, groups.length > 1, newTab, batch))}
                </ul>
            </div>
        );
    }
}
